import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAdminAuth } from '../../context/AdminAuthContext';
import axios from 'axios';
import { 
  BarChart3, Users, Globe, FileText, TrendingUp, 
  ArrowLeft, Calendar, MousePointer, RefreshCw, CheckCircle, ExternalLink
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AnalyticsDashboard = () => {
  const { admin, loading: authLoading } = useAdminAuth();
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState('7d');
  const [analyticsConfig, setAnalyticsConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !admin) {
      navigate('/admin/login');
    }
  }, [admin, authLoading, navigate]);

  // Fetch fresh content directly from API
  useEffect(() => {
    const fetchAnalyticsConfig = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/admin/content`, { withCredentials: true });
        setAnalyticsConfig(response.data?.analytics || {});
      } catch (err) {
        // Analytics config load failed - use defaults
        setAnalyticsConfig({});
      } finally {
        setLoading(false);
      }
    };

    if (admin) {
      fetchAnalyticsConfig();
    }
  }, [admin]);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  // Check if analytics is configured
  const ga4Id = analyticsConfig?.ga4Id;
  const clarityId = analyticsConfig?.clarityId;
  const isAnalyticsConfigured = ga4Id && ga4Id.length > 0;

  // Placeholder data for the dashboard
  const stats = [
    { 
      label: 'Total Visitors', 
      value: '—', 
      change: '—', 
      icon: Users,
      color: 'bg-blue-50 text-blue-600'
    },
    { 
      label: 'Page Views', 
      value: '—', 
      change: '—', 
      icon: FileText,
      color: 'bg-green-50 text-green-600'
    },
    { 
      label: 'Avg. Session', 
      value: '—', 
      change: '—', 
      icon: TrendingUp,
      color: 'bg-purple-50 text-purple-600'
    },
    { 
      label: 'Conversions', 
      value: '—', 
      change: '—', 
      icon: MousePointer,
      color: 'bg-amber-50 text-amber-600'
    }
  ];

  const topPages = [
    { page: 'Homepage', views: '—', percentage: '—' },
    { page: 'About', views: '—', percentage: '—' },
    { page: 'Program', views: '—', percentage: '—' },
    { page: 'Contact', views: '—', percentage: '—' }
  ];

  const topCountries = [
    { country: 'Japan', visitors: '—', flag: '🇯🇵' },
    { country: 'South Korea', visitors: '—', flag: '🇰🇷' },
    { country: 'United States', visitors: '—', flag: '🇺🇸' },
    { country: 'United Kingdom', visitors: '—', flag: '🇬🇧' }
  ];

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link 
                to="/admin/dashboard" 
                className="flex items-center gap-2 text-slate-600 hover:text-slate-900"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Dashboard
              </Link>
            </div>
            <div className="flex items-center gap-3">
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
              >
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="90d">Last 90 days</option>
              </select>
              <button className="flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-slate-900 border border-slate-200 rounded-lg">
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Title */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
            <BarChart3 className="w-7 h-7" />
            Analytics Dashboard
          </h1>
          <p className="text-slate-500 mt-1">Monitor your website performance and visitor insights</p>
        </div>

        {/* Status Notice - Changes based on configuration */}
        {isAnalyticsConfigured ? (
          <div className="mb-8 bg-green-50 border border-green-200 rounded-xl p-5">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-green-800">Analytics Connected</p>
                <p className="text-sm text-green-700 mt-1">
                  Google Analytics 4 is configured with ID: <code className="bg-green-100 px-1.5 py-0.5 rounded text-xs font-mono">{ga4Id}</code>
                  {clarityId && (
                    <span className="ml-2">• Clarity ID: <code className="bg-green-100 px-1.5 py-0.5 rounded text-xs font-mono">{clarityId}</code></span>
                  )}
                </p>
                <p className="text-sm text-green-600 mt-2">
                  <strong>Note:</strong> This dashboard shows placeholder data. To view real analytics:
                </p>
                <div className="flex gap-3 mt-3">
                  <a 
                    href="https://analytics.google.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-green-700 hover:text-green-900 bg-green-100 px-3 py-1.5 rounded-lg"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    Open Google Analytics
                  </a>
                  {clarityId && (
                    <a 
                      href="https://clarity.microsoft.com" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-green-700 hover:text-green-900 bg-green-100 px-3 py-1.5 rounded-lg"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      Open Microsoft Clarity
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="mb-8 bg-amber-50 border border-amber-200 rounded-xl p-5">
            <div className="flex items-start gap-3">
              <Calendar className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">Analytics Setup Required</p>
                <p className="text-sm text-amber-700 mt-1">
                  To view real analytics data, please add your Google Analytics 4 Measurement ID in the 
                  <Link to="/admin/dashboard" className="underline ml-1">Admin Settings → Analytics tab</Link>.
                  Once configured, this dashboard will display your actual visitor data.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {stats.map((stat) => {
            const Icon = stat.icon;
            return (
              <div key={stat.label} className="bg-white rounded-xl p-6 border border-slate-200">
                <div className="flex items-center justify-between mb-4">
                  <div className={`w-10 h-10 ${stat.color} rounded-lg flex items-center justify-center`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <span className="text-xs text-slate-400">{stat.change}</span>
                </div>
                <p className="text-2xl font-bold text-slate-900">{stat.value}</p>
                <p className="text-sm text-slate-500">{stat.label}</p>
              </div>
            );
          })}
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Pages */}
          <div className="bg-white rounded-xl p-6 border border-slate-200">
            <div className="flex items-center gap-2 mb-6">
              <FileText className="w-5 h-5 text-slate-600" />
              <h2 className="text-lg font-semibold text-slate-900">Top Pages</h2>
            </div>
            <div className="space-y-4">
              {topPages.map((page) => (
                <div key={page.page} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                  <span className="text-sm text-slate-700">{page.page}</span>
                  <div className="flex items-center gap-4">
                    <span className="text-sm font-medium text-slate-900">{page.views}</span>
                    <span className="text-xs text-slate-400 w-12 text-right">{page.percentage}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Top Countries */}
          <div className="bg-white rounded-xl p-6 border border-slate-200">
            <div className="flex items-center gap-2 mb-6">
              <Globe className="w-5 h-5 text-slate-600" />
              <h2 className="text-lg font-semibold text-slate-900">Top Countries</h2>
            </div>
            <div className="space-y-4">
              {topCountries.map((country) => (
                <div key={country.country} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                  <div className="flex items-center gap-3">
                    <span className="text-lg">{country.flag}</span>
                    <span className="text-sm text-slate-700">{country.country}</span>
                  </div>
                  <span className="text-sm font-medium text-slate-900">{country.visitors}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Conversions Section */}
        <div className="mt-6 bg-white rounded-xl p-6 border border-slate-200">
          <div className="flex items-center gap-2 mb-6">
            <MousePointer className="w-5 h-5 text-slate-600" />
            <h2 className="text-lg font-semibold text-slate-900">Conversion Events</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { event: 'Contact Form Submitted', count: '—' },
              { event: 'Trial Lesson Booked', count: '—' },
              { event: 'Book Trial Clicked', count: '—' },
              { event: 'Contact Us Clicked', count: '—' }
            ].map((item) => (
              <div key={item.event} className="bg-slate-50 rounded-lg p-4">
                <p className="text-xs text-slate-500 mb-1">{item.event}</p>
                <p className="text-xl font-bold text-slate-900">{item.count}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
